import type {
  PortfolioAnswer,
  PortfolioBuilderDto,
  PortfolioSide,
  PortfolioStrategyDto,
  PortfolioStrategySideDto,
} from "../../api/hooks/usePortfolioBuilder";
import { formatMonthYear } from "../../lib/dates";

/** "Can your account sell short?" is the page's own question, so its entry never goes to the API. */
export const SIDE_QUESTION_ID = "side";
export const CHECK_ID = "check";
export const REFINE_ID = "refine";

export const SIZES = [2, 3, 4, 5, 6] as const;
export const DEFAULT_SIZE = 4;
export const DEFAULT_AMOUNT_THOUSANDS = 10;
export const DEFAULT_START_MONTHS_BACK = 24;

// The figure keys the page shows by name. Every other figure only steers the questions.
export const FIGURE = {
  avgYear: "avg_year_pct",
  realDd: "real_dd_pct",
  worstMonth: "worst_month_pct",
  tradesPerYear: "trades_per_year",
  winRate: "win_rate_pct",
  winMonths: "win_months_pct",
} as const;

/** The side the answers chose. Long and short until the side question is answered. */
export function sideOf(history: PortfolioAnswer[]): PortfolioSide {
  const answer = history.find((entry) => entry.id === SIDE_QUESTION_ID);
  return answer?.v === "b" ? "long-only" : "long-and-short";
}

/** The Scr.Enums.StrategyDirection a side is sold as, for its mark and its words. */
export function directionOf(side: PortfolioSide): number {
  return side === "long-only" ? 1 : 3;
}

export function strategySide(
  strategy: PortfolioStrategyDto,
  side: PortfolioSide,
): PortfolioStrategySideDto | null {
  return side === "long-only" ? strategy.longOnly : strategy.longAndShort;
}

/** How many portfolios a group holds. Zero where it holds none or the revision never counted it. */
export function portfolioCount(
  builder: Pick<PortfolioBuilderDto, "counts">,
  side: PortfolioSide,
  size: number,
  exclude: number,
): number {
  return builder.counts[side]?.[String(size)]?.[exclude] ?? 0;
}

/**
 * The size chosen, or the largest this side has portfolios of when it has none that size. A side can
 * carry fewer strategies than the other, so the customer's choice is kept for when they switch back.
 */
export function fittingSize(
  builder: Pick<PortfolioBuilderDto, "counts">,
  side: PortfolioSide,
  chosen: number,
  exclude: number,
): number {
  if (portfolioCount(builder, side, chosen, exclude) > 0) return chosen;
  const largest = [...SIZES].reverse().find((size) => portfolioCount(builder, side, size, exclude) > 0);
  return largest ?? chosen;
}

const MINUS = "−";

export const formatInt = (value: number): string => Math.round(value).toLocaleString("en-US");

/** An average year: "+343%". */
export const formatAvgYear = (value: number): string =>
  `${value >= 0 ? "+" : MINUS}${formatInt(Math.abs(value))}%`;

/** A drawdown is a fall whichever sign it is stored with: "−6.8%". */
export const formatDrawdown = (value: number): string => `${MINUS}${Math.abs(value).toFixed(1)}%`;

export const formatSignedOnePlace = (value: number): string =>
  `${value >= 0 ? "+" : MINUS}${Math.abs(value).toFixed(1)}%`;

export const formatSignedWhole = (value: number): string =>
  `${value >= 0 ? "+" : MINUS}${formatInt(Math.abs(value))}%`;

/** Money on the growth card. Shorthand from $10k up, unless `exact`. */
export function formatMoney(value: number, exact = false): string {
  if (exact || value < 1e4) return `$${formatInt(value)}`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1).replace(/\.0$/, "")}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(value >= 1e7 ? 0 : 1).replace(/\.0$/, "")}M`;
  return `$${(value / 1e3).toFixed(value >= 1e5 ? 0 : 1).replace(/\.0$/, "")}k`;
}

/** The amount box reads in thousands: "10", "2.5". */
export const formatThousands = (thousands: number): string =>
  (Math.round(thousands * 10) / 10).toLocaleString("en-US");

/** What the amount box holds, as thousands, kept to something the growth line can be drawn for. */
export function parseThousands(typed: string): number {
  const value = parseFloat(typed.replace(/[^0-9.]/g, ""));
  return Math.min(1e6, Math.max(0.1, value || 0.1));
}

/** Trades a year in the words a customer counts them in, as an i18n key under panel.trades. */
export function tradesPhrase(perYear: number): { key: string; count?: number } {
  const perWeek = perYear / 52;
  const perMonth = perYear / 12;
  if (perWeek >= 1.75) return { key: "perWeek", count: Math.round(perWeek) };
  if (perWeek >= 0.75) return { key: "oneAWeek" };
  if (perMonth >= 1.5) return { key: "perMonth", count: Math.round(perMonth) };
  if (perMonth >= 0.75) return { key: "oneAMonth" };
  return { key: "lessThanMonthly" };
}

/** Months are "2024-09". */
export function addMonths(month: string, count: number): string {
  const [year, monthOfYear] = month.split("-").map(Number);
  const index = year * 12 + (monthOfYear - 1) + count;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
}

export function monthsBetween(from: string, to: string): number {
  const [fromYear, fromMonth] = from.split("-").map(Number);
  const [toYear, toMonth] = to.split("-").map(Number);
  return (toYear - fromYear) * 12 + (toMonth - fromMonth);
}

/** "Sep 2024". */
export const formatMonth = (month: string): string => formatMonthYear(`${month}-01`);

/** Round-number gridlines between `low` and `high`, about `target` of them. */
export function niceTicks(low: number, high: number, target: number): number[] {
  const raw = (high - low) / target || 1;
  const power = 10 ** Math.floor(Math.log10(raw));
  const ratio = raw / power;
  const step = (ratio < 1.5 ? 1 : ratio < 3 ? 2 : ratio < 7 ? 5 : 10) * power;
  const ticks: number[] = [];
  for (let value = Math.ceil(low / step) * step; value <= high + 1e-9; value += step) {
    ticks.push(Number(value.toFixed(6)));
  }
  return ticks;
}
